import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import GoogleReviews from '../components/GoogleReviews';
import RDVBookingBubble from '../components/RDVBookingBubble';
import ProcessSlider from '../components/ProcessSlider';
import {
  CalendarCheck, ArrowRight, Wrench, Settings, CircleDot, Wind, Stethoscope,
  ClipboardCheck, Cog, MapPin, Phone, Clock, MessageCircle, ShieldCheck, CheckCircle, Award,
} from 'lucide-react';

// ──────────────────────────────────────────────────────────────────────────────
// Image assets
// ──────────────────────────────────────────────────────────────────────────────
const HERO_IMG = 'https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/jbzie3cv_en-tete.png';

const PROCESS_STEPS = [
  {
    id: 'rdv',
    num: 1,
    title: 'Prise de RDV en ligne',
    subtitle: 'Choisissez votre véhicule, la date et l\u2019heure depuis votre canapé. Confirmation immédiate.',
    dotLabel: 'Rendez-vous',
    image: 'https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/709qcrgw_rendez-vous.png',
    alt: 'Prise de rendez-vous en ligne sur smartphone — Carrosserie Meyrin',
  },
  {
    id: 'depot',
    num: 2,
    title: 'Dépose de votre véhicule',
    subtitle: 'Accueil personnalisé à l\u2019atelier de Meyrin. Notre équipe prend en charge votre voiture.',
    dotLabel: 'Dépose',
    image: 'https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/khmy3rvm_Prise-en-charge-vehicule.png',
    alt: 'Prise en charge du véhicule par un technicien CarMaintenance à Meyrin',
  },
  {
    id: 'diagnostic',
    num: 3,
    title: 'Diagnostic & contrôle',
    subtitle: 'Inspection complète sur pont avec tablette connectée — Expert 1217 agréé toutes assurances.',
    dotLabel: 'Diagnostic',
    image: 'https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/77xnlugq_contro%CC%82le-du-vehicule.png',
    alt: 'Diagnostic atelier par un mécanicien CarMaintenance Meyrin — Expert 1217',
  },
  {
    id: 'devis',
    num: 4,
    title: 'Devis transparent par SMS',
    subtitle: 'Vous recevez le devis détaillé par SMS. Validez-le en un clic, sans surprise.',
    dotLabel: 'Devis SMS',
    image: 'https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/wd5b7w7o_devis-SMS.png',
    alt: 'Notification SMS du devis CarMaintenance sur iPhone',
  },
  {
    id: 'travaux',
    num: 5,
    title: 'Travaux après validation',
    subtitle: 'Aucune intervention sans votre accord. Peinture auto Genève, mécanique, carrosserie.',
    dotLabel: 'Travaux',
    image: 'https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/s4hp4kdb_contro%CC%82le-du-vehicule.png',
    alt: 'Réalisation des travaux par un technicien CarMaintenance après validation',
  },
  {
    id: 'restitution',
    num: 6,
    title: 'Restitution',
    subtitle: 'Votre véhicule prêt à reprendre la route. Transparence, expertise, proximité.',
    dotLabel: 'Restitution',
    image: 'https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/yp2etc30_Vehicule-pret.png',
    alt: 'Remise des clés au client CarMaintenance Meyrin — Merci pour votre confiance',
  },
  {
    id: 'abonnement',
    num: 7,
    title: 'Et après ? L\u2019abonnement',
    subtitle: 'L\u2019entretien connecté dès 39 CHF / mois — sans engagement. Roulez l\u2019esprit tranquille.',
    dotLabel: 'Abonnement',
    image: 'https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/yu2oe4ka_IMG_5110.png',
    alt: 'Abonnement CarMaintenance dès 39 CHF par mois — entretien connecté',
  },
];

const ATELIER_PHOTOS = [
  {
    src: 'https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/a6qtzwvr_IMG_2040.jpeg',
    caption: 'Façade du Garage — Carrosserie Meyrin (1217)',
    alt: 'Façade du garage CarMaintenance — Carrosserie Meyrin Expert 1217, agréé toutes assurances',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/zilktkw9_IMG_2527.jpeg',
    caption: 'Cabine de Peinture Neuve — Peinture auto Genève',
    alt: 'Cabine de peinture auto neuve — Peinture auto Genève, atelier Expert 1217 Meyrin',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/72qg2gkh_IMG_0487.jpeg',
    caption: 'Atelier Carrosserie — Expert 1217 Meyrin',
    alt: 'Atelier carrosserie professionnel à Meyrin — Carrosserie Meyrin Expert 1217',
  },
];

const SERVICES = [
  { icon: Wrench, label: 'Mécanique', route: '/garage-meyrin/mecanique', slug: 'mecanique' },
  { icon: Settings, label: 'Carrosserie', route: '/garage-meyrin/carrosserie', slug: 'carrosserie' },
  { icon: CircleDot, label: 'Pneus & Géométrie', route: '/garage-meyrin/pneus', slug: 'pneus' },
  { icon: Wind, label: 'Climatisation', route: '/garage-meyrin/climatisation', slug: 'climatisation' },
  { icon: Stethoscope, label: 'Diagnostic', route: '/garage-meyrin/diagnostic', slug: 'diagnostic' },
  { icon: ClipboardCheck, label: 'Contrôle technique', route: '/garage-meyrin/expertise', slug: 'expertise' },
  { icon: Cog, label: 'Boîtes de vitesses', route: '/garage-meyrin/boites-vitesses', slug: 'boites-vitesses' },
];

const GarageMeyrinV2Page = () => {
  const [rdvOpen, setRdvOpen] = useState(false);

  return (
    <>
      <Helmet>
        <title>Carrosserie Meyrin &amp; Peinture auto Genève — Expert 1217 | CarMaintenance Meyrin</title>
        <meta name="description" content="Carrosserie Meyrin et Peinture auto Genève — Expert 1217, agréé toutes assurances. Découvrez en images notre parcours de prise en charge : RDV en ligne, diagnostic, devis SMS, travaux validés, restitution. Abonnement entretien dès 39 CHF/mois." />
        <link rel="preload" as="image" href={HERO_IMG} />
        <meta name="keywords" content="Carrosserie Meyrin, Peinture auto Genève, Expert 1217, garage meyrin, abonnement entretien automobile, UPSA, carrosserie plus, garage plus, devis transparent SMS, prise de rendez-vous en ligne, atelier carrosserie 1217" />
        <meta property="og:title" content="Carrosserie Meyrin &amp; Peinture auto Genève — Expert 1217 | CarMaintenance" />
        <meta property="og:description" content="Notre parcours en images : RDV en ligne, diagnostic, devis SMS, travaux validés, restitution. Abonnement dès 39 CHF/mois." />
        <meta property="og:image" content={HERO_IMG} />
        <link rel="canonical" href="https://car-main.ch/garage-meyrin" />
        <link rel="alternate" hrefLang="fr-CH" href="https://car-main.ch/garage-meyrin" />
        <link rel="alternate" hrefLang="x-default" href="https://car-main.ch/garage-meyrin" />
        <meta name="geo.region" content="CH-GE" />
        <meta name="geo.placename" content="Meyrin, Genève" />
        <meta name="geo.position" content="46.239567;6.0773604" />
        <meta name="ICBM" content="46.239567, 6.0773604" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'AutoRepair',
          '@id': 'https://car-main.ch/garage-meyrin#business',
          name: 'CarMaintenance Meyrin',
          alternateName: 'Car Maintenance — Garage & Carrosserie Meyrin',
          image: HERO_IMG,
          logo: 'https://car-main.ch/logo-carmaintenance.png',
          description: 'Carrosserie Meyrin & Peinture auto Genève — Expert 1217 agréé toutes assurances. Mécanique, diagnostic, pneus, climatisation, boîtes de vitesses.',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Rue des Lattes 62',
            addressLocality: 'Meyrin',
            addressRegion: 'GE',
            postalCode: '1217',
            addressCountry: 'CH',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 46.239567,
            longitude: 6.0773604,
          },
          hasMap: 'https://maps.google.com/?cid=3100184985273365445',
          telephone: '+41229809393',
          email: 'info@car-main.ch',
          url: 'https://car-main.ch/garage-meyrin',
          priceRange: 'CHF',
          areaServed: [
            { '@type': 'City', name: 'Meyrin' },
            { '@type': 'City', name: 'Genève' },
            { '@type': 'City', name: 'Vernier' },
            { '@type': 'City', name: 'Grand-Saconnex' },
            { '@type': 'City', name: 'Châtelaine' },
            { '@type': 'AdministrativeArea', name: 'Canton de Genève' },
          ],
          openingHoursSpecification: [
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '08:00', closes: '12:00' },
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '13:30', closes: '18:00' },
          ],
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.8',
            reviewCount: '13',
            bestRating: '5',
            worstRating: '1',
          },
          sameAs: [
            'https://maps.google.com/?cid=3100184985273365445',
          ],
          makesOffer: [
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Mécanique & Entretien', url: 'https://car-main.ch/garage-meyrin/mecanique' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Diagnostic électronique', url: 'https://car-main.ch/garage-meyrin/diagnostic' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Pneus & Géométrie', url: 'https://car-main.ch/garage-meyrin/pneus' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Climatisation', url: 'https://car-main.ch/garage-meyrin/climatisation' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Préparation Expertise & Contrôle', url: 'https://car-main.ch/garage-meyrin/expertise' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Carrosserie & Peinture', url: 'https://car-main.ch/garage-meyrin/carrosserie' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Boîtes de vitesses', url: 'https://car-main.ch/garage-meyrin/boites-vitesses' } },
          ],
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://car-main.ch/' },
            { '@type': 'ListItem', position: 2, name: 'Garage Meyrin', item: 'https://car-main.ch/garage-meyrin' },
          ],
        })}</script>
      </Helmet>

      <div className="bg-white text-gray-900">
        {/* ───────────────────────── HERO ───────────────────────── */}
        <section
          className="relative min-h-[78vh] bg-cover bg-center bg-no-repeat flex items-center"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.5) 55%, rgba(0,0,0,0.25) 100%), url('${HERO_IMG}')`,
          }}
          role="img"
          aria-label="Façade CarMaintenance Meyrin — Carrosserie & Mécanique Expert 1217, agréé toutes assurances"
          data-testid="meyrin-v2-hero"
        >
          <div className="container mx-auto px-6 py-20 sm:py-28 max-w-6xl">
            <div className="max-w-2xl text-white">
              <div className="inline-flex items-center gap-2 bg-[#52a675]/20 border border-[#52a675]/50 text-[#a8e3c0] px-3 py-1.5 rounded-full text-xs font-semibold backdrop-blur-sm mb-6">
                <ShieldCheck className="w-3.5 h-3.5" />
                Expert 1217 · Agréé toutes assurances
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
                CarMaintenance
                <span className="block text-[#52a675]">Meyrin</span>
              </h1>
              {/* SEO-only — hidden from view, read by Google & screen readers */}
              <h2 className="sr-only">
                Carrosserie Meyrin &amp; Peinture auto Genève — Expert 1217, garage agréé toutes assurances, membre UPSA, Carrosserie Plus et Garage Plus.
              </h2>
              <p className="mt-6 text-base sm:text-lg text-gray-200 leading-relaxed max-w-xl">
                Votre véhicule, notre expertise. Un parcours simple et transparent, du premier clic à la remise des clés.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={() => setRdvOpen(true)}
                  className="bg-[#52a675] hover:bg-[#3d8a5c] text-white px-8 py-6 text-base sm:text-lg shadow-2xl hover:shadow-[0_20px_50px_rgba(82,166,117,0.5)] hover:-translate-y-0.5 transition-all duration-300 ring-2 ring-white/20 font-semibold"
                  data-testid="meyrin-v2-hero-rdv-button"
                >
                  <CalendarCheck className="mr-2" />
                  Prendre rendez-vous en 2 clics
                </Button>
                <a href="#process">
                  <Button
                    size="lg"
                    variant="outline"
                    className="bg-white/5 backdrop-blur-sm border-white/40 text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-base sm:text-lg w-full sm:w-auto"
                    data-testid="meyrin-v2-process-button"
                  >
                    Découvrir le parcours
                    <ArrowRight className="ml-2" />
                  </Button>
                </a>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs sm:text-sm text-gray-300">
                <span className="inline-flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[#52a675]" /> UPSA</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[#52a675]" /> Carrosserie Plus</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[#52a675]" /> Garage Plus</span>
                <span className="inline-flex items-center gap-1.5"><Award className="w-4 h-4 text-[#52a675]" /> Toutes marques</span>
              </div>
            </div>
          </div>
        </section>

        {/* ───────────────────────── PROCESS SLIDER ───────────────────────── */}
        <section id="process" className="py-16 sm:py-24 bg-gradient-to-b from-[#f0f9f4] via-white to-white">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <div className="text-center mb-12 max-w-2xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-[#52a675]/10 border border-[#52a675]/20 text-[#3d8a5c] px-3 py-1 rounded-full text-xs font-semibold mb-4">
                Notre processus
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
                De votre canapé <span className="text-[#52a675]">au volant</span>, en 6 étapes.
              </h2>
              <p className="text-gray-600 mt-4 text-base sm:text-lg">
                Glissez pour découvrir notre parcours — chaque étape pensée pour votre tranquillité.
              </p>
            </div>

            <ProcessSlider steps={PROCESS_STEPS} autoplayMs={5000} />
          </div>
        </section>

        {/* ───────────────────────── NOTRE ATELIER ───────────────────────── */}
        <section className="py-16 sm:py-20 bg-white" data-testid="meyrin-v2-atelier">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
                Notre <span className="text-[#52a675]">Atelier</span>
              </h2>
              <p className="text-base sm:text-lg text-gray-600">
                Découvrez nos installations modernes et professionnelles.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {ATELIER_PHOTOS.map((photo) => (
                <div
                  key={photo.src}
                  className="rounded-2xl overflow-hidden shadow-xl h-72 sm:h-80 bg-cover bg-center group ring-1 ring-gray-100"
                  style={{ backgroundImage: `url('${photo.src}')` }}
                  role="img"
                  aria-label={photo.alt}
                >
                  <div className="h-full bg-gradient-to-t from-black/65 via-black/10 to-transparent flex items-end p-6 group-hover:from-black/75 transition-colors">
                    <p className="text-white font-semibold text-sm sm:text-base leading-tight">{photo.caption}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ───────────────────────── ABONNEMENT PITCH ───────────────────────── */}
        <section className="py-16 sm:py-20 bg-gradient-to-br from-[#e8f5ef] to-[#f0f9f4]" data-testid="meyrin-v2-abonnement-section">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <div className="rounded-3xl overflow-hidden bg-white ring-1 ring-gray-200 shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/yu2oe4ka_IMG_5110.png"
                alt="L'entretien connecté CarMaintenance — Abonnement dès 39 CHF/mois sans engagement"
                className="w-full h-auto block"
                loading="lazy"
                decoding="async"
              />
              <div className="px-6 sm:px-10 py-8 sm:py-10 text-center sm:text-left sm:flex sm:items-center sm:justify-between gap-6 bg-white">
                <div className="max-w-xl">
                  <h3 className="text-2xl sm:text-3xl font-bold leading-tight text-gray-900">
                    Et si vous passiez à <span className="text-[#52a675]">l&apos;abonnement&nbsp;?</span>
                  </h3>
                  <p className="text-gray-600 mt-3 text-sm sm:text-base">
                    Entretien connecté sans engagement, dès 39 CHF/mois. Sérénité totale, pas de mauvaise surprise.
                  </p>
                </div>
                <div className="mt-6 sm:mt-0 shrink-0">
                  <Link to="/abonnements">
                    <Button
                      size="lg"
                      className="bg-[#52a675] hover:bg-[#3d8a5c] text-white px-8 py-6 text-base font-semibold shadow-xl hover:shadow-[0_20px_50px_rgba(82,166,117,0.5)] hover:-translate-y-0.5 transition-all"
                      data-testid="meyrin-v2-abonnement-cta"
                    >
                      Découvrir l&apos;abonnement
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ───────────────────────── SERVICES CONDENSÉS ───────────────────────── */}
        <section className="py-12 sm:py-16 bg-white border-y border-gray-100">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <h2 className="text-center text-xs sm:text-sm uppercase tracking-[0.2em] text-gray-500 mb-8">
              Nos services
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4 sm:gap-5">
              {SERVICES.map(({ icon: Icon, label, route, slug }) => (
                <Link
                  to={route}
                  key={slug}
                  className="group flex flex-col items-center text-center gap-3 p-4 rounded-2xl hover:bg-gray-50 hover:-translate-y-0.5 transition-all duration-300 ring-1 ring-transparent hover:ring-[#52a675]/20"
                  data-testid={`meyrin-v2-service-${slug}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#52a675]/10 ring-1 ring-[#52a675]/20 flex items-center justify-center group-hover:bg-[#52a675]/15 group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6 text-[#52a675]" />
                  </div>
                  <span className="text-xs sm:text-sm text-gray-700 font-medium leading-tight group-hover:text-[#3d8a5c]">{label}</span>
                  <span className="text-[10px] text-[#52a675] font-semibold opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-0.5">
                    Voir plus <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ───────────────────────── INFOS PRATIQUES ───────────────────────── */}
        <section className="py-16 sm:py-20 bg-gray-50" data-testid="meyrin-v2-infos">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm p-6 sm:p-8">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">Venez nous voir</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[#52a675] shrink-0 mt-0.5" />
                    <div className="text-sm sm:text-base text-gray-700">
                      Rue des Lattes 62<br />
                      1217 Meyrin · Genève
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-[#52a675] shrink-0 mt-0.5" />
                    <div className="text-sm sm:text-base text-gray-700">
                      Lun–Ven : 08:00–18:00<br />
                      Sam : sur RDV
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 pt-4">
                    <a href="tel:+41229809393" className="flex-1">
                      <Button variant="outline" className="w-full" data-testid="meyrin-v2-phone-btn">
                        <Phone className="mr-2 w-4 h-4" />
                        022 980 93 93
                      </Button>
                    </a>
                    <a href="https://wa.me/41782662662" target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button variant="outline" className="w-full border-[#52a675] bg-[#52a675]/5 text-[#3d8a5c] hover:bg-[#52a675] hover:text-white" data-testid="meyrin-v2-whatsapp-btn">
                        <MessageCircle className="mr-2 w-4 h-4" />
                        WhatsApp
                      </Button>
                    </a>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl overflow-hidden ring-1 ring-gray-200 min-h-[320px] bg-white">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2762.5!2d6.07!3d46.23!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sChemin%20des%20Lattes%2062%2C%201217%20Meyrin!5e0!3m2!1sfr!2sch!4v1234567890"
                  title="Plan d'accès — CarMaintenance Meyrin"
                  className="w-full h-full min-h-[320px]"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen=""
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ───────────────────────── GOOGLE REVIEWS ───────────────────────── */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <GoogleReviews placeId="ChIJixuwCgpjjEcRxfM3kdERBis" />
          </div>
        </section>

        {/* ───────────────────────── FOOTER CTA ───────────────────────── */}
        <section className="py-16 sm:py-20 bg-gradient-to-br from-[#52a675] via-[#3d8a5c] to-[#2e6d47]">
          <div className="container mx-auto px-4 sm:px-6 max-w-3xl text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
              Prêt à confier votre véhicule à Meyrin ?
            </h2>
            <p className="text-white/90 mt-3 text-base sm:text-lg">
              Réservez votre créneau en moins de 60 secondes.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => setRdvOpen(true)}
                className="bg-white text-[#3d8a5c] hover:bg-gray-100 px-8 py-6 text-base sm:text-lg font-semibold shadow-2xl hover:-translate-y-0.5 transition-all"
                data-testid="meyrin-v2-footer-rdv-button"
              >
                <CalendarCheck className="mr-2" />
                Prendre rendez-vous
              </Button>
              <a href="tel:+41229809393">
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-transparent border-white/60 text-white hover:bg-white hover:text-[#3d8a5c] px-8 py-6 text-base sm:text-lg w-full sm:w-auto"
                  data-testid="meyrin-v2-footer-phone-button"
                >
                  <Phone className="mr-2 w-4 h-4" />
                  022 980 93 93
                </Button>
              </a>
            </div>
          </div>
        </section>
      </div>

      <RDVBookingBubble open={rdvOpen} onOpenChange={setRdvOpen} />
    </>
  );
};

export default GarageMeyrinV2Page;
